/* eslint-disable @typescript-eslint/no-unused-vars */
import { IconMinus, IconPencil, IconPlus } from "@tabler/icons-react";
import InputField from "../../../components/common/InputField";
import ListView from "../../../components/common/ListView";
import { useEffect, useState } from "react";
import Section from "../../../components/common/Section";
import { useMerchantAllListData } from "../../../apis/merchantApis";
import {
  useWeightCreateMutation,
  useWeightMutation,
  useWeightUpdateMutation,
} from "../../../apis/shippingApis";
import toast from "react-hot-toast";
import formatDate from "../../../utilities/helper";
import NoDataFound from "../../../components/common/NoDataFound";
import Pagination from "../../../components/common/Pagination";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import NewSelectInput from "../../../components/NewSelectInput";
import { useLanguage } from "../../../context/LanguageContext";

interface WeightRange {
  from: string;
  to: string;
}

export default function Weight() {
  const [forClient, setForClient] = useState(true);
  const [client, setClient] = useState("");
  const [id, setId] = useState("");
  const [clientError, setClientError] = useState("");
  const [weightRanges, setWeightRanges] = useState<WeightRange[]>([
    { from: "", to: "" },
  ]);
  const [error, setError] = useState<Record<string, string>>({});
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [mode, setMode] = useState<"add" | "edit" | null>(null);
  const { t } = useLanguage();

  const { data: clients } = useMerchantAllListData();
  const { data, refetch, isLoading, isFetching } = useWeightMutation(
    pageNo,
    limit
  );
  const { mutate: createMutate, isPending: isCreating } =
    useWeightCreateMutation();
  const { mutate: updateMutate, isPending: isUpdating } =
    useWeightUpdateMutation();

  const [disableForClientCheckbox, setDisableForClientCheckbox] =
    useState(false);

  useEffect(() => {
    if (!data?.results) return;

    const hasByDefault = data.results.some(
      (row: any) => row.clientName === null
    );

    setDisableForClientCheckbox(hasByDefault);
  }, [data]);

  // Reset form
  const resetForm = () => {
    setForClient(true);
    setClient("");
    setId("");
    setClientError("");
    setWeightRanges([{ from: "", to: "" }]);
    setError({});
    setMode(null);
  };

  // Validation for ranges
  const validateRanges = (): boolean => {
    const newErrors: Record<string, string> = {};
    let hasError = false;

    // 1. Basic field-level checks
    weightRanges.forEach((range, index) => {
      if (!range.from || range.from.trim() === "") {
        newErrors[`from_${index}`] = t("This field is required");
        hasError = true;
      } else if (isNaN(Number(range.from))) {
        newErrors[`from_${index}`] = t("Must be a valid number");
        hasError = true;
      }

      if (!range.to || range.to.trim() === "") {
        newErrors[`to_${index}`] = t("This field is required");
        hasError = true;
      } else {
        const toLower = range.to.trim().toLowerCase();
        if (toLower !== "flat" && isNaN(Number(range.to))) {
          newErrors[`to_${index}`] = t('Must be a valid number or "flat"');
          hasError = true;
        } else if (toLower !== "flat" && Number(range.from) >= Number(range.to)) {
          newErrors[`to_${index}`] = t("To must be greater than From");
          hasError = true;
        }
      }
    });

    if (hasError) {
      setError(newErrors);
      return false;
    }

    // 2. Overlap & Duplicate checks (only check if basic validation passes)
    const normalize = (v: string) => (v.trim().toLowerCase() === "flat" ? Infinity : Number(v));

    // Duplicate check
    const seen = new Set<string>();
    for (let i = 0; i < weightRanges.length; i++) {
      const r = weightRanges[i];
      const key = `${r.from.trim()}-${r.to.trim().toLowerCase()}`;
      if (seen.has(key)) {
        newErrors[`to_${i}`] = t("Duplicate range detected");
        newErrors[`from_${i}`] = t("Duplicate range detected");
        hasError = true;
      }
      seen.add(key);
    }

    // Overlap check
    const sortedRanges = weightRanges
      .map((r, originalIndex) => ({
        from: Number(r.from),
        to: normalize(r.to),
        originalIndex,
      }))
      .sort((a, b) => a.from - b.from);

    for (let i = 1; i < sortedRanges.length; i++) {
      const prev = sortedRanges[i - 1];
      const curr = sortedRanges[i];

      if (curr.from < prev.to) {
        newErrors[`from_${curr.originalIndex}`] = t("Overlaps with another range");
        newErrors[`to_${prev.originalIndex}`] = t("Overlaps with another range");
        hasError = true;
      }
    }

    setError(newErrors);
    return !hasError;
  };

  // Handle Add Range
  const handleAddRange = () => {
    if (validateRanges()) {
      setWeightRanges([...weightRanges, { from: "", to: "" }]);
    }
  };

  // Handle Remove Range
  const handleRemoveRange = (index: number) => {
    const newRanges = weightRanges.filter((_, i) => i !== index);
    setWeightRanges(newRanges);

    // Clean up errors for removed field
    const newErrors = { ...error };
    delete newErrors[`from_${index}`];
    delete newErrors[`to_${index}`];
    setError(newErrors);
  };

  // Handle Input Change
  const handleRangeChange = (
    index: number,
    field: "from" | "to",
    value: string
  ) => {
    const newRanges = [...weightRanges];
    newRanges[index][field] = value;
    setWeightRanges(newRanges);

    // Clear error on change
    const newErrors = { ...error };
    delete newErrors[`${field}_${index}`];
    setError(newErrors);
  };

  // Handle Create
  const handleSubmit = () => {
    if (!validateRanges()) return;

    if (forClient && !client) {
      setClientError(t("Please select a client"));
      return;
    }
    setClientError("");

    const payload = {
      clientForId: forClient ? client : null,
      ranges: weightRanges.map((r) => ({
        from: Number(r.from),
        to: r.to?.toLowerCase() === "flat" ? "flat" : Number(r.to),
      })),
    };

    createMutate(payload, {
      onSuccess: () => {
        toast.success(t("Weight added successfully"));
        resetForm();
        refetch();
      },
      onError: (err: any) => {
        toast.error(err || t("Failed to create weight"));
      },
    });
  };

  // Handle Update
  const handleUpdate = () => {
    if (!validateRanges()) return;

    if (forClient && !client) {
      setClientError(t("Please select a client"));
      return;
    }
    setClientError("");

    const payload = {
      clientForId: forClient ? client : null,
      ranges: weightRanges.map((r) => ({
        from: Number(r.from),
        to: r.to?.toLowerCase() === "flat" ? "flat" : Number(r.to),
      })),
    };

    updateMutate(
      { id, payload },
      {
        onSuccess: () => {
          toast.success(t("Weight updated successfully"));
          resetForm();
          refetch();
        },
        onError: (err: any) => {
          toast.error(err?.message || t("Failed to update weight"));
        },
      }
    );
  };

  // Handle Edit Click
  const handleEdit = (row: any) => {
    setMode("edit");
    setId(row.id);
    setClient(row.clientForId || "");
    setForClient(!!row.clientForId);
    setWeightRanges(
      row.ranges.map((r: any) => ({
        from: String(r.from),
        to: r.to === "flat" ? "flat" : String(r.to),
      }))
    );
    setError({});
    setClientError("");
  };

  // Pagination
  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  const isFlatPresent = weightRanges.some(
    (r) => r.to?.toLowerCase()?.trim() === "flat"
  );
  const isSubmitting = isCreating || isUpdating;

  return (
    <div className="space-y-4 border border-[#d2d2d2] rounded-[20px] p-4 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] mb-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium text-[#212121]">{t("Weight Info")}</h2>
        <button
          onClick={() => {
            if (mode) {
              resetForm();
            } else {
              setMode("add");
              setWeightRanges([{ from: "", to: "" }]);
            }
          }}
          className="bg-[#FBD037] text-[#212121] text-sm font-semibold p-2 rounded-full"
        >
          {mode ? (
            <IconMinus stroke={1.5} size={20} />
          ) : (
            <IconPlus stroke={1.5} size={20} />
          )}
        </button>
      </div>

      {/* Form */}
      {mode && (
        <div className="bg-[#FFFFFF] p-4 rounded-lg space-y-4 border border-[#D2D2D2]">
          {/* Client Checkbox */}
          <div>
            <div className="flex items-center gap-3">
              <label className="relative flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  id="forClient"
                  checked={forClient}
                  onChange={() => setForClient(!forClient)}
                  disabled={disableForClientCheckbox}
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037]
                            bg-white checked:bg-[#FBD037] checked:text-white"
                />
                <span className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                  ✓
                </span>
              </label>

              <label
                htmlFor="forClient"
                className="text-sm text-[#1E293B] cursor-pointer"
              >
                {t("For Client")}
              </label>
            </div>

            {forClient && (
              <div className="mt-3 border-b">
                <NewSelectInput
                  className="pb-3"
                  label={t("Select Client")}
                  selectOptions={
                    clients?.map((c: any) => ({
                      id: c.id,
                      value: c.id,
                      text: c.name,
                    })) || []
                  }
                  value={client}
                  setValue={(val: string) => {
                    setClient(val);
                    setClientError("");
                  }}
                  name={""}
                  error={clientError}
                />
                {clientError && (
                  <small className="text-[#DC3545] text-xs">
                    {clientError}
                  </small>
                )}
              </div>
            )}
          </div>

          {/* Weight Ranges */}
          <Section
            mainClassName=""
            className="text-sm pb-4"
            title={t("Weight Range")}
          >
            <div className="space-y-4">
              {weightRanges.map((range, index) => (
                <div key={index} className="flex items-start gap-3">
                  {/* From */}
                  <div className="flex-1">
                    <label
                      className={`text-sm text-[#1E293B] ${
                        index === 0 ? "" : "invisible"
                      }`}
                    >
                      {t("From (kg)")}
                    </label>
                    <InputField
                      className="mt-1"
                      label=""
                      type="number"
                      placeholder={t("Enter from kg")}
                      value={range.from}
                      onChange={(e) =>
                        handleRangeChange(index, "from", e.target.value)
                      }
                    />
                    {error[`from_${index}`] && (
                      <small className="text-[#DC3545] text-xs">
                        {error[`from_${index}`]}
                      </small>
                    )}
                  </div>

                  {/* To */}
                  <div className="flex-1">
                    <label
                      className={`text-sm text-[#1E293B] ${
                        index === 0 ? "" : "invisible"
                      }`}
                    >
                      {t("To (kg)")}
                    </label>
                    <InputField
                      className="mt-1"
                      label=""
                      type="text"
                      placeholder={t('Enter only "flat"')}
                      value={range.to}
                      onChange={(e) =>
                        handleRangeChange(index, "to", e.target.value)
                      }
                    />
                    {error[`to_${index}`] && (
                      <small className="text-[#DC3545] text-xs">
                        {error[`to_${index}`]}
                      </small>
                    )}
                  </div>

                  {/* Add / Remove Button */}
                  <div className="mt-7 flex gap-2">
                    {weightRanges.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveRange(index)}
                        className="bg-[#FBD037]/20 border border-[#FBD037] rounded-full p-2 hover:bg-red-100"
                      >
                        <IconMinus size={18} className="text-black" />
                      </button>
                    )}
                    {index === weightRanges.length - 1 && !isFlatPresent && (
                      <button
                        type="button"
                        onClick={handleAddRange}
                        className="bg-[#FBD037]/20 border border-[#FBD037] rounded-full p-2 hover:bg-[#FBD037]/30"
                      >
                        <IconPlus size={18} className="text-black" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Submit Button */}
            <div className="mt-6 flex gap-2">
              <button
                onClick={mode === "add" ? handleSubmit : handleUpdate}
                disabled={isSubmitting || !isFlatPresent}
                className="bg-[#FBD037] text-[#212121] text-sm font-semibold px-5 py-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <span className="animate-pulse">{t("Processing...")}</span>
                  </>
                ) : mode === "add" ? (
                  t("Save")
                ) : (
                  t("Update")
                )}
              </button>
              <button
                onClick={resetForm}
                className="border border-[#D2D2D2] text-[#212121] text-sm font-semibold px-5 py-2 rounded-full"
              >
                {t("Cancel")}
              </button>
            </div>
          </Section>
        </div>
      )}

      {/* Table */}
      <div className="-mx-4">
        {isLoading || isFetching ? (
          <TableSkeleton
            mainClass="bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5]"
            subClassName=""
            columns={[
              "#",
              t("Client"),
              t("Created By"),
              t("Created Type"),
              t("Date"),
              t("Actions"),
            ]}
            rows={6}
          />
        ) : data?.results?.length ? (
          <ListView
            mainDivClassName="bg-transparent"
            tdClassName="rounded-r-none rounded-l-none"
            trClassName="hover:bg-[#FBD037]/20 transition-colors"
            mailDivClassName="p-0 bg-transparent"
            thClassName="border-t"
            data={data.results}
            columns={[
              {
                label: "#",
                render: (_: any, i: number) => (pageNo - 1) * limit + i + 1,
              },
              {
                label: t("Client Name"),
                render: (c: any) => c?.clientName || t("By Default"),
              },
              {
                label: t("Created By"),
                render: (c: any) => c?.createdByName || "--",
              },
              {
                label: t("Created Type"),
                render: (c: any) => c?.createdByType || "—",
              },
              {
                label: t("Date"),
                render: (c: any) => formatDate(c?.created_at),
              },
              {
                label: t("Actions"),
                render: (row) => (
                  <div className="flex justify-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(row);
                      }}
                      className="p-2 border border-[#D2D2D2] rounded-full hover:bg-[#FBD037]/10 transition-colors"
                      title={t("Edit Weight")}
                    >
                      <IconPencil size={20} className="text-[#212121]" />
                    </button>
                  </div>
                ),
              },
            ]}
          />
        ) : (
          <NoDataFound
            subClassName=""
            mainClassName="bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] rounded-b-3xl"
          />
        )}

        {/* Pagination */}
        {data?.totalPages > 1 && (
          <div className="flex justify-center items-center py-4">
            <Pagination
              currentPage={pageNo}
              totalPages={data.totalPages}
              fetchData={handlePaginationFetch}
            />
          </div>
        )}
      </div>
    </div>
  );
}
